import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../../config/firebase";
import { collection, getDocs } from "firebase/firestore";
import Calendar from "./Calendar";
import { motion } from "framer-motion";

// Define event categories
const categories = ["all", "Competition", "Training", "Workshop", "Exhibition"];

// Add CSS for animation
const animateFadeInDown = `
@keyframes fadeInDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
.animate-fade-in-down {
  animation: fadeInDown 0.6s ease-out;
}
`;

// Helper function to get background color based on event category
const getCategoryColor = (category) => {
  switch(category) {
    case 'Competition':
      return 'from-red-500/70 to-red-600/70 dark:from-red-700/70 dark:to-red-800/70';
    case 'Training':
      return 'from-blue-500/70 to-blue-600/70 dark:from-blue-700/70 dark:to-blue-800/70';
    case 'Workshop':
      return 'from-green-500/70 to-green-600/70 dark:from-green-700/70 dark:to-green-800/70';
    case 'Exhibition':
      return 'from-purple-500/70 to-purple-600/70 dark:from-purple-700/70 dark:to-purple-800/70';
    default:
      return 'from-blue-500/70 to-blue-600/70 dark:from-blue-700/70 dark:to-blue-800/70';
  }
};

const Events = () => {
  // Add animation style to head
  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.textContent = animateFadeInDown;
    document.head.appendChild(styleElement);
    
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedCategory, setSelectedCategory] = useState("all");
  const navigate = useNavigate();

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const eventsCollection = collection(db, "events");
        const querySnapshot = await getDocs(eventsCollection);
        const eventsList = querySnapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            // Ensure image property exists and is properly formatted
            image: data.image || null
          };
        });

        // Sort events by date in descending order (newest first)
        eventsList.sort((a, b) => {
          const dateA = new Date(a.date);
          const dateB = new Date(b.date);
          return dateB - dateA; // Descending order (newest first)
        });

        console.log("Fetched events:", eventsList); // Debug log to see event data
        setEvents(eventsList);
      } catch (error) {
        console.error("Error fetching events:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, []);

  const filteredEvents =
    selectedCategory === "all"
      ? events
      : events.filter((event) => event.category === selectedCategory);

  // Separate upcoming and past events
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison

  // Helper function to parse date string without timezone conversion
  const parseLocalDate = (dateString) => {
    if (!dateString) return null;
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day); // month is 0-indexed
  };

  const upcomingEvents = filteredEvents.filter(event => {
    const eventDate = parseLocalDate(event.date);
    return eventDate >= today;
  });
  const pastEvents = filteredEvents.filter(event => {
    const eventDate = parseLocalDate(event.date);
    return eventDate < today;
  });

  const handleEventClick = (event) => {
    navigate(`/events/${event.id}`);
  };

  const formatTime = (timeString) => {
    if (!timeString) return "";

    // Parse the time string (assuming format like "14:00")
    const [hours, minutes] = timeString.split(":");
    const date = new Date();
    date.setHours(parseInt(hours), parseInt(minutes));

    return date
      .toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      })
      .replace(/\s+/g, "")
      .toLowerCase(); // Remove space between time and AM/PM
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Hero Section - Dynamic Showcase Design */}
      <section className="relative overflow-hidden bg-gradient-to-br from-blue-900 via-indigo-900 to-gray-900 text-white">
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden">
          {/* Geometric shapes */}
          <motion.div 
            className="absolute top-[10%] right-[15%] w-64 h-64 rounded-full bg-blue-500/5 blur-3xl"
            animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div 
            className="absolute bottom-[20%] left-[10%] w-80 h-80 rounded-full bg-indigo-400/5 blur-3xl"
            animate={{ scale: [1.2, 1, 1.2], opacity: [0.2, 0.4, 0.2] }}
            transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
          />
          
          {/* Dynamic lines */}
          <div className="absolute inset-0 opacity-10">
            <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                  <path d="M 0 10 L 40 10" fill="none" stroke="white" strokeWidth="0.5" />
                  <path d="M 10 0 L 10 40" fill="none" stroke="white" strokeWidth="0.5" />
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#grid)" />
            </svg>
          </div>
        </div>

        {/* Main content container */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 py-16 md:py-24 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            {/* Left content - Text and CTA */}
            <div className="lg:col-span-6 lg:pr-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              >
                <div className="inline-block mb-4 px-4 py-1 rounded-full bg-blue-500/20 backdrop-blur-sm border border-blue-500/30">
                  <span className="text-blue-300 text-sm font-semibold tracking-wider">
                    CALENDAR & SCHEDULE
                  </span>
                </div>
                
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                  Upcoming{" "}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-300 via-blue-400 to-indigo-300">
                    Events
                  </span>
                </h1>
                
                <p className="text-lg md:text-xl mb-8 text-gray-200 font-medium leading-relaxed max-w-xl">
                  Stay up to date with our latest events and activities. Find competitions, training sessions, and more.
                </p>
                
                {/* Category Filter with improved design */}
                <div className="flex flex-wrap gap-3 md:gap-4 mt-6">
                  {categories.map((category) => (
                    <button
                      key={category}
                      onClick={() => setSelectedCategory(category)}
                      className={`px-3 sm:px-4 md:px-6 py-2 md:py-3 rounded-full text-xs sm:text-sm font-semibold transition-all
                        ${
                          selectedCategory === category
                            ? "bg-blue-600 text-white shadow-lg scale-105"
                            : "bg-white/40 text-white hover:bg-white/50 backdrop-blur-sm hover:scale-105"
                        }`}
                    >
                      {category.charAt(0).toUpperCase() + category.slice(1)}
                    </button>
                  ))}
                </div>
              </motion.div>
            </div>
            
            {/* Right content - Hero image with animated elements */}
            <div className="lg:col-span-6 relative">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 1, delay: 0.2 }}
                className="relative"
              >
                {/* Main image container with mask effect */}
                <div className="relative overflow-hidden rounded-2xl shadow-2xl border border-white/10 bg-gradient-to-br from-blue-500/10 to-indigo-500/10 backdrop-blur-sm">
                  <div className="aspect-[4/3] relative overflow-hidden">
                    <img 
                      src="/eventhero.png" 
                      alt="AZ Boys Gymnastics Events" 
                      className="w-full h-full object-cover object-center"
                    />
                    
                    {/* Overlay gradient */}
                    <div className="absolute inset-0 bg-gradient-to-tr from-blue-900/40 via-transparent to-transparent"></div>
                    
                    {/* Animated highlight */}
                    <motion.div 
                      className="absolute inset-0 bg-gradient-to-r from-transparent via-blue-400/20 to-transparent" 
                      style={{ width: '200%', x: '-100%' }}
                      animate={{ x: '100%' }}
                      transition={{ duration: 2.5, repeat: Infinity, repeatType: 'loop', ease: 'linear', repeatDelay: 5 }}
                    />
                  </div>
                </div>
                
                {/* Decorative elements */}
                <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-blue-500/30 backdrop-blur-xl border border-blue-400/30 flex items-center justify-center">
                  <span className="text-white font-bold text-lg">AZ</span>
                </div>
                
                <motion.div 
                  className="absolute -bottom-4 -left-4 p-4 bg-white/10 backdrop-blur-xl rounded-lg border border-white/20 shadow-xl"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.8 }}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div>
                      <div className="text-white font-medium">Region 1</div>
                      <div className="text-xs text-gray-300">Championship Gymnastics</div>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </div>
        
        {/* Scroll indicator */}
        <motion.div
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2 hidden lg:flex flex-col items-center"
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 1.5 }}
        >
          <span className="text-sm text-gray-300 mb-2">Scroll to explore</span>
          <svg
            className="w-6 h-6 text-blue-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 14l-7 7m0 0l-7-7m7 7V3"
            />
          </svg>
        </motion.div>
      </section>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-10 md:py-16">
        {/* Calendar Section with improved styling */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl dark:shadow-gray-900/10 p-4 md:p-6 mb-10 md:mb-16 border border-gray-100 dark:border-gray-700/50">
          <Calendar
            events={filteredEvents}
            currentMonth={currentMonth}
            setCurrentMonth={setCurrentMonth}
            onEventClick={handleEventClick}
          />
        </div>

        {/* Upcoming Events Section */}
        {upcomingEvents.length > 0 && (
          <div className="mb-12 md:mb-16">
            <div className="mb-6 md:mb-10 text-center">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-3 md:mb-4">
                Upcoming Events
              </h2>
              <div className="w-16 md:w-20 h-1 bg-green-600 mx-auto rounded-full"></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {upcomingEvents.map((event) => (
                <div
                  key={event.id}
                  onClick={() => handleEventClick(event)}
                  className="group bg-white dark:bg-gray-800 rounded-xl overflow-hidden shadow-lg dark:shadow-gray-900/10
                    transition-all duration-300 hover:-translate-y-2 hover:shadow-xl cursor-pointer
                    border border-gray-100 dark:border-gray-700/50 hover:border-green-500/50 dark:hover:border-green-500/30"
                >
                  {/* Card Header with Date and Image */}
                  <div className="relative h-40 sm:h-48 bg-blue-100 dark:bg-blue-900/30 overflow-hidden">
                    {/* Event Image with Fallback */}
                    {event.image ? (
                      <img
                        src={event.image}
                        alt={event.title}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.style.display = 'none';
                          e.target.parentNode.querySelector('.image-fallback').style.display = 'flex';
                        }}
                      />
                    ) : null}

                    <div
                      className={`image-fallback w-full h-full flex items-center justify-center ${event.image ? 'hidden' : 'flex'} bg-gradient-to-r ${getCategoryColor(event.category)}`}
                    >
                      <span className="text-white text-lg font-semibold opacity-80">{event.category}</span>
                    </div>

                    {/* Date Badge */}
                    <div className="absolute top-3 right-3 bg-white dark:bg-gray-800 text-green-600 dark:text-green-400
                      rounded-lg shadow-md px-2 py-1 sm:px-3 sm:py-2 font-semibold text-xs sm:text-sm z-10">
                      {parseLocalDate(event.date)?.toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                      })}
                    </div>

                    {/* Category Badge with Gradient Overlay */}
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3 sm:p-4 z-10">
                      <span className="px-2 sm:px-3 py-1 bg-blue-600 text-white text-xs font-medium rounded-full">
                        {event.category}
                      </span>
                    </div>
                  </div>

                  {/* Card Content */}
                  <div className="p-4 sm:p-6">
                    <h3 className="text-xl font-bold mb-3 text-gray-900 dark:text-white group-hover:text-green-600 dark:group-hover:text-green-400 transition-colors">
                      {event.title}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300 mb-4 line-clamp-3">
                      {event.description}
                    </p>
                    <div className="flex justify-between items-center pt-4 border-t border-gray-100 dark:border-gray-700/50">
                      <span className="text-sm font-medium text-gray-500 dark:text-gray-400 flex items-center">
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {formatTime(event.time)} - {formatTime(event.endTime)}
                      </span>
                      <span
                        className="inline-flex items-center text-green-600 dark:text-green-400 font-semibold
                        group-hover:text-green-700 dark:group-hover:text-green-300"
                      >
                        Details
                        <svg
                          className="w-4 h-4 ml-2 transform transition-transform group-hover:translate-x-1"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Past Events Section */}
        {pastEvents.length > 0 && (
          <div className="mb-12 md:mb-16">
            <div className="mb-6 md:mb-10 text-center">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-3 md:mb-4">
                Past Events
              </h2>
              <div className="w-16 md:w-20 h-1 bg-gray-600 mx-auto rounded-full"></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {pastEvents.map((event) => (
                <div
                  key={event.id}
                  onClick={() => handleEventClick(event)}
                  className="group bg-white dark:bg-gray-800 rounded-xl overflow-hidden shadow-lg dark:shadow-gray-900/10
                    transition-all duration-300 hover:-translate-y-2 hover:shadow-xl cursor-pointer
                    border border-gray-100 dark:border-gray-700/50 hover:border-gray-500/50 dark:hover:border-gray-500/30 opacity-75"
                >
                  {/* Card Header with Date and Image */}
                  <div className="relative h-40 sm:h-48 bg-blue-100 dark:bg-blue-900/30 overflow-hidden">
                    {/* Event Image with Fallback */}
                    {event.image ? (
                      <img
                        src={event.image}
                        alt={event.title}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.style.display = 'none';
                          e.target.parentNode.querySelector('.image-fallback').style.display = 'flex';
                        }}
                      />
                    ) : null}

                    <div
                      className={`image-fallback w-full h-full flex items-center justify-center ${event.image ? 'hidden' : 'flex'} bg-gradient-to-r ${getCategoryColor(event.category)}`}
                    >
                      <span className="text-white text-lg font-semibold opacity-80">{event.category}</span>
                    </div>

                    {/* Date Badge */}
                    <div className="absolute top-3 right-3 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400
                      rounded-lg shadow-md px-2 py-1 sm:px-3 sm:py-2 font-semibold text-xs sm:text-sm z-10">
                      {parseLocalDate(event.date)?.toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                      })}
                    </div>

                    {/* Category Badge with Gradient Overlay */}
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3 sm:p-4 z-10">
                      <span className="px-2 sm:px-3 py-1 bg-blue-600 text-white text-xs font-medium rounded-full">
                        {event.category}
                      </span>
                    </div>
                  </div>

                  {/* Card Content */}
                  <div className="p-4 sm:p-6">
                    <h3 className="text-xl font-bold mb-3 text-gray-900 dark:text-white group-hover:text-gray-700 dark:group-hover:text-gray-300 transition-colors">
                      {event.title}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300 mb-4 line-clamp-3">
                      {event.description}
                    </p>
                    <div className="flex justify-between items-center pt-4 border-t border-gray-100 dark:border-gray-700/50">
                      <span className="text-sm font-medium text-gray-500 dark:text-gray-400 flex items-center">
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {formatTime(event.time)} - {formatTime(event.endTime)}
                      </span>
                      <span
                        className="inline-flex items-center text-gray-600 dark:text-gray-400 font-semibold
                        group-hover:text-gray-700 dark:group-hover:text-gray-300"
                      >
                        Details
                        <svg
                          className="w-4 h-4 ml-2 transform transition-transform group-hover:translate-x-1"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* No Events Found Message */}
        {upcomingEvents.length === 0 && pastEvents.length === 0 && (
          <div className="col-span-1 md:col-span-2 lg:col-span-3 text-center py-10 md:py-16 bg-white dark:bg-gray-800 rounded-xl shadow-lg">
            <svg className="w-12 h-12 md:w-16 md:h-16 mx-auto text-gray-300 dark:text-gray-600 mb-3 md:mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <p className="text-lg md:text-xl text-gray-500 dark:text-gray-400">
              No events found for this category
            </p>
            <button
              onClick={() => setSelectedCategory("all")}
              className="mt-3 md:mt-4 px-4 md:px-6 py-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors text-sm md:text-base"
            >
              View all events
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Events;
